import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Paper,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Chip,
  Skeleton,
  Alert,
  Fab,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Avatar
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import BackButton from '../components/BackButton';

// Configuración de niveles de usuario con colores
const nivelesConfig = {
  'Administrador Sistema': { color: '#E53935', bgColor: '#FFEBEE', icon: AdminIcon },
  'Administrador Empresa': { color: '#7B1FA2', bgColor: '#F3E5F5', icon: AdminIcon },
  'Supervisor': { color: '#FB8C00', bgColor: '#FFF3E0', icon: BusinessIcon },
  'Trabajador': { color: '#1E88E5', bgColor: '#E3F2FD', icon: PersonIcon },
};

// Componente de tarjeta para vista móvil
const UsuarioCard = ({ usuario, onEdit, onDelete }) => {
  const nivelConfig = nivelesConfig[usuario.det_nivel_usuario] || { color: '#757575', bgColor: '#F5F5F5', icon: PersonIcon };
  const IconComponent = nivelConfig.icon;

  return (
    <Card sx={{
      mb: 2,
      borderRadius: 3,
      border: '1px solid rgba(26, 35, 126, 0.1)',
      borderLeft: `4px solid ${nivelConfig.color}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(26, 35, 126, 0.15)'
      }
    }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar
            sx={{
              width: 50,
              height: 50,
              background: `linear-gradient(135deg, ${nivelConfig.color} 0%, ${nivelConfig.color}dd 100%)`,
              color: 'white',
              fontWeight: 'bold'
            }}
          >
            {usuario.username?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
              {usuario.username}
            </Typography>
            <Chip
              icon={<IconComponent sx={{ fontSize: 14 }} />}
              label={usuario.det_nivel_usuario}
              size="small"
              sx={{
                bgcolor: nivelConfig.bgColor,
                color: nivelConfig.color,
                fontWeight: 'bold',
                mt: 0.5
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PublicIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {usuario.det_pais}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {usuario.nombre_empresa}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Tooltip title="Editar usuario">
            <IconButton
              size="small"
              onClick={() => onEdit(usuario)}
              sx={{ bgcolor: 'primary.light', color: 'white', '&:hover': { bgcolor: 'primary.main' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar usuario">
            <IconButton
              size="small"
              onClick={() => onDelete(usuario.id_usuario)}
              sx={{ bgcolor: 'error.light', color: 'white', '&:hover': { bgcolor: 'error.main' } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );
};

function UsuariosHomeAdmin() {
  const [usuarios, setUsuarios] = useState([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [orderBy, setOrderBy] = useState('username');
  const [order, setOrder] = useState('asc');
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchUsuarios();
  }, []);

  useEffect(() => {
    const filterAndSortUsuarios = () => {
      let filtered = [...usuarios];

      if (searchTerm) {
        filtered = filtered.filter(usuario =>
          Object.values(usuario)
            .join(' ')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
      }

      filtered.sort((a, b) => {
        const isAsc = order === 'asc';
        if (a[orderBy] < b[orderBy]) return isAsc ? -1 : 1;
        if (a[orderBy] > b[orderBy]) return isAsc ? 1 : -1;
        return 0;
      });

      setFilteredUsuarios(filtered);
      setPage(0);
    };

    filterAndSortUsuarios();
  }, [usuarios, searchTerm, order, orderBy]);

  const fetchUsuarios = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/usuarios-home`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setUsuarios(response.data);
      setError(null);
    } catch (error) {
      console.error('Error al obtener los usuarios:', error);
      setError('Error al cargar los usuarios. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este usuario?')) {
      try {
        await axios.delete(`${config.API_BASE_URL}/api/usuarios-home/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchUsuarios();
      } catch (error) {
        console.error('Error al eliminar el usuario:', error);
        setError('Error al eliminar el usuario.');
      }
    }
  };

  const handleEdit = (usuario) => {
    navigate(`/home/usuarios/editar/${usuario.id_usuario}`);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: { xs: 2, sm: 3, md: 4 } }}>
        <Skeleton variant="text" width="60%" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>

      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BackButton />
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              component="h1"
              sx={{
                fontWeight: 'bold',
                color: '#1A237E',
                mb: 0.5
              }}
            >
              🔐 Gestión de Accesos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Activa, desactiva o cambia contraseñas de los usuarios de tu empresa
            </Typography>
          </Box>
        </Box>

        {!isMobile && (
          <Chip
            label="Los usuarios se crean desde Trabajadores"
            size="small"
            sx={{ bgcolor: '#E8EAF6', color: '#1A237E', fontWeight: 600 }}
          />
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Barra de búsqueda */}
      <Box sx={{ mb: 3 }}>
        <TextField
          variant="outlined"
          fullWidth={isMobile}
          size="small"
          placeholder="Buscar por nombre, nivel, país, empresa..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#4FC3F7' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: isMobile ? '100%' : 400,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: '#4FC3F7',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1A237E',
              },
            }
          }}
        />
      </Box>

      {/* Vista móvil - Cards */}
      {isMobile ? (
        <Box>
          {filteredUsuarios
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((usuario) => (
              <UsuarioCard
                key={usuario.id_usuario}
                usuario={usuario}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}

          {filteredUsuarios.length > rowsPerPage && (
            <Paper sx={{ mt: 2, borderRadius: 2 }}>
              <TablePagination
                rowsPerPageOptions={[20]}
                component="div"
                count={filteredUsuarios.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
                labelRowsPerPage="Filas por página:"
              />
            </Paper>
          )}
        </Box>
      ) : (
        /* Vista desktop - Tabla mejorada */
        <Paper sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(26, 35, 126, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{
                background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)'
              }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Usuario
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'det_nivel_usuario'}
                      direction={orderBy === 'det_nivel_usuario' ? order : 'asc'}
                      onClick={createSortHandler('det_nivel_usuario')}
                      sx={{
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      Nivel
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    País
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Empresa
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsuarios
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((usuario) => {
                    const nivelConfig = nivelesConfig[usuario.det_nivel_usuario] || { color: '#757575', bgColor: '#F5F5F5', icon: PersonIcon };
                    const IconComponent = nivelConfig.icon;

                    return (
                      <TableRow
                        key={usuario.id_usuario}
                        sx={{
                          '&:nth-of-type(even)': {
                            backgroundColor: 'rgba(26, 35, 126, 0.02)'
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(79, 195, 247, 0.1)',
                            transform: 'scale(1.01)',
                            transition: 'all 0.2s ease'
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                background: `linear-gradient(135deg, ${nivelConfig.color} 0%, ${nivelConfig.color}dd 100%)`,
                                color: 'white',
                                fontSize: '0.8rem',
                                fontWeight: 'bold'
                              }}
                            >
                              {usuario.username?.[0]?.toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1A237E' }}>
                              {usuario.username}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={<IconComponent sx={{ fontSize: 14 }} />}
                            label={usuario.det_nivel_usuario}
                            size="small"
                            sx={{
                              bgcolor: nivelConfig.bgColor,
                              color: nivelConfig.color,
                              fontWeight: 'bold'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PublicIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {usuario.det_pais}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {usuario.nombre_empresa}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Editar usuario">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(usuario)}
                                sx={{
                                  bgcolor: 'primary.light',
                                  color: 'white',
                                  '&:hover': { bgcolor: 'primary.main' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar usuario">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(usuario.id_usuario)}
                                sx={{
                                  bgcolor: 'error.light',
                                  color: 'white',
                                  '&:hover': { bgcolor: 'error.main' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[20]}
            component="div"
            count={filteredUsuarios.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            labelRowsPerPage="Filas por página:"
            sx={{
              borderTop: '1px solid rgba(26, 35, 126, 0.1)',
              backgroundColor: 'rgba(26, 35, 126, 0.02)'
            }}
          />
        </Paper>
      )}

      {/* Info para móvil */}
      {isMobile && (
        <Box sx={{ position: 'fixed', bottom: 20, left: 20, right: 20,
          bgcolor: '#E8EAF6', borderRadius: 3, p: 1.5, textAlign: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10 }}>
          <Typography sx={{ fontSize: 12, color: '#1A237E', fontWeight: 600 }}>
            Para crear usuarios, ve a Trabajadores
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default UsuariosHomeAdmin;
